/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { FC, ReactElement, useEffect, useContext, useState } from 'react';
import { MatchesContext, PrefferencesContext } from '../../context/context';
import { getMatches } from '../../services/http/httpService';
import { IFootballMatch, IFootballLiveMatchScore } from '../../interfaces/interfaces';
import { REFRESH_INTERVAL, WIDGET_WIDTH } from '../../config/config';
import TableLoader from '../loader/loader';
import {
  trans,
  getRealGameTimestamp,
  getCompetitionIdQuery,
  getSeasonIdQuery,
  getDays,
  getMonths,
  getStageId
} from '../../helpers/helperFunctions';

const Matches: FC = (): ReactElement => {
  const { selectedDisplayOption } = useContext(MatchesContext);

  const { language, widgetWidth } = useContext(PrefferencesContext);

  const [loading, setLoading] = useState<boolean>(true);

  const [matches, setMatches] = useState<IFootballMatch[]>([]);

  // Set loading to false when matches are fetched
  useEffect(() => {
    if (matches.length) {
      setLoading(false);
    }
  }, [matches]);

  // Fetches standings when a competition is selected manually or on load
  useEffect(() => {
    if (!language) return undefined;
    setLoading(true);
    const competitionId = getCompetitionIdQuery();
    const seasonId = getSeasonIdQuery();
    getMatches(language, competitionId, seasonId)
      .then((response) => {
        const mapResponse = response.map((item) => ({
          ...item,
          date: getRealGameTimestamp(item.date)
        }));
        setMatches(mapResponse);
      })
      .catch((e) => {
        console.log('Error fetching standings');
        setLoading(false);
      });

    // Register interval to re-fetch matches
    const standingsInterval = setInterval(() => {
      getMatches(language, competitionId, seasonId)
        .then((response) => {
          const mapResponse = response.map((item) => ({
            ...item,
            date: getRealGameTimestamp(item.date)
          }));
          setMatches(mapResponse);
        })
        .catch((e) => {
          console.log('Error fetching standings');
        });
    }, REFRESH_INTERVAL);

    return () => clearInterval(standingsInterval);
  }, [language]);

  return <>{createMatches(matches, loading, widgetWidth, selectedDisplayOption)}</>;
};

function createMatches(
  matches: IFootballMatch[],
  loading: boolean,
  widgetWidth: number,
  selectedDisplayOption: string
): ReactElement[] {
  const stageId = getStageId();

  if (selectedDisplayOption === trans('gamesByDate')) {
    return createDateGrouppings(matches, loading, widgetWidth, stageId);
  }
  return createGroups(matches, loading, widgetWidth, stageId);
}

function createGroups(
  footbalMatches: IFootballMatch[],
  loading: boolean,
  widgetWidth: number,
  stageId: string
) {
  let matches: IFootballMatch[] = [];
  matches = footbalMatches.sort((a, b) => +new Date(a.date) - +new Date(b.date));

  if (stageId) {
    matches = footbalMatches.filter((item) => item.stageInfo.stageId === stageId);
  }

  const groups = Array.from(
    new Set(
      matches
        .filter((match) => match.stageInfo.groupMatchday !== '0')
        .map((match) => match.stageInfo.stageName)
    )
  );

  groups.sort((a, b) => {
    if (a < b) return -1;
    if (a > b) return 1;
    return 0;
  });

  const knockouts = Array.from(
    new Set(
      matches
        .filter((match) => match.stageInfo.groupMatchday === '0')
        .map((match) => match.stageInfo.stageName)
    )
  );

  const stages = groups.concat(knockouts);

  return stages.map((group, index) => {
    const matchesFilteredByGroup = matches.filter((match) => {
      return match.stageInfo.stageName === group;
    });
    return (
      <div key={group + index}>
        <div className="stage-name">{group}</div>
        {createGrouppedMatches(matchesFilteredByGroup, loading, widgetWidth, true)}
      </div>
    );
  });
}

function createDateGrouppings(
  footbalMatches: IFootballMatch[],
  loading: boolean,
  widgetWidth: number,
  stageId: string
) {
  let matches: IFootballMatch[] = [];
  matches = footbalMatches.sort((a, b) => +new Date(a.date) - +new Date(b.date));

  if (stageId) {
    matches = footbalMatches.filter((item) => item.stageInfo.stageId === stageId);
  }

  const groupMatchDays = Array.from(
    new Set(
      matches
        .filter((match) => match.stageInfo.groupMatchday !== '0')
        .map((match) => {
          return match.stageInfo.groupMatchday;
        })
    )
  );

  const groupPhaseMatches = groupMatchDays.map((matchDay) => {
    const stageMatches = matches.filter((match) => {
      return match.stageInfo.groupMatchday === matchDay;
    });
    return { stageName: `${trans('groupPhase')}, ${matchDay}. ${trans('round')}`, stageMatches };
  });

  const knockoutStageNames = Array.from(
    new Set(
      matches
        .filter((match) => {
          return match.stageInfo.groupMatchday === '0';
        })
        .map((match) => {
          return match.stageInfo.stageName;
        })
    )
  );

  const knockoutStageMatchesGroupped = knockoutStageNames.map((knockoutName) => {
    const stageMatches = matches.filter((match) => {
      return match.stageInfo.stageName === knockoutName;
    });
    return { stageName: knockoutName, stageMatches };
  });
  const matchesByStage = groupPhaseMatches.concat(knockoutStageMatchesGroupped);

  return matchesByStage.map((stage, index) => {
    const { stageName, stageMatches } = stage;

    const dateGrouppings = Array.from(
      new Set(
        stageMatches.map((match) => {
          const newDate = new Date(match.date);
          const dayName = getDays()[newDate.getDay()];
          const date = newDate.getDate();
          const monthName = getMonths()[newDate.getMonth()];
          return `${dayName}, ${date}. ${monthName}`;
        })
      )
    );
    const matchesByDate = dateGrouppings.map((matchDate, dateIndex) => {
      const matchesFilteredByDate = stageMatches.filter((match) => {
        const newDate = new Date(match.date);
        const dayName = getDays()[newDate.getDay()];
        const date = newDate.getDate();
        const monthName = getMonths()[newDate.getMonth()];
        return `${dayName}, ${date}. ${monthName}` === matchDate;
      });
      return (
        <div key={matchDate + dateIndex}>
          <div className="match-date">{matchDate}</div>
          {createGrouppedMatches(matchesFilteredByDate, loading, widgetWidth)}
        </div>
      );
    });
    return (
      <div key={stageName + index}>
        <div className="stage-name">{stageName}</div>
        <div className="matches-by-date">{matchesByDate}</div>
      </div>
    );
  });
}

function createGrouppedMatches(
  matches: IFootballMatch[],
  loading: boolean,
  widgetWidth: number,
  isGroupeFilter?: boolean
): ReactElement {
  // + operator is before Date due typescript warning issue https://github.com/microsoft/TypeScript/issues/5710
  // basiclly convert to number
  matches.sort((a, b) => +new Date(a.date) - +new Date(b.date));

  return loading ? (
    <TableLoader viewBox="10 10 1200 720" />
  ) : (
    <div className={`matches-container ${isGroupeFilter ? 'matches-container-groupe' : ''}`}>
      <table className="matches-table">
        <tbody>
          {matches.map((row, index) => {
            const {
              home,
              homeShort,
              homeMicro,
              showHomeTeam,
              away,
              awayShort,
              awayMicro,
              showAwayTeam,
              teamLogoUrlHome,
              teamLogoUrlAway,
              date,
              liveScore,
              postponed
            } = row;
            const homeName =
              widgetWidth <= WIDGET_WIDTH && showHomeTeam === 'yes' && homeMicro
                ? homeMicro
                : homeShort;
            const awayName =
              widgetWidth <= WIDGET_WIDTH && showAwayTeam === 'yes' && awayMicro
                ? awayMicro
                : awayShort;
            return (
              <tr
                className={`${
                  showHomeTeam === 'no' || showAwayTeam === 'no' ? 'elimination-stage' : ''
                }`}
                key={`${home}${away}${index}${Math.random()}`}>
                <td>
                  {showHomeTeam === 'yes' && <img src={teamLogoUrlHome} alt="Team logo" />}
                  {homeName}
                </td>
                {createScoreRow(date, liveScore, postponed)}
                <td>
                  {awayName}
                  {showAwayTeam === 'yes' && <img src={teamLogoUrlAway} alt="Team logo" />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function createScoreRow(
  gameDate: string | number,
  liveScore: IFootballLiveMatchScore,
  postponed: boolean
) {
  if (postponed) return <td>{trans('postponed')}</td>;
  const notStarted = gameDate > Date.now();
  if (notStarted) {
    const newDate = new Date(gameDate);
    const date = newDate.getDate();
    const month = newDate.getMonth() + 1;
    const hour = newDate.getHours();
    const minutes = newDate.getMinutes() !== 0 ? newDate.getMinutes() : '00';
    const displayTime = `${date}.${month}. - ${hour}:${minutes}`;
    return <td className="match-time">{displayTime}</td>;
  }
  return <td className="match-score">{createScoreString(liveScore)}</td>;
}

function createScoreString(liveScore: IFootballLiveMatchScore) {
  if (!liveScore.currentScore) {
    return (
      <div>
        {!liveScore?.penaltyScore ? (
          <p>
            {liveScore?.extraTime?.home}:{liveScore?.extraTime?.away} ({liveScore?.halfTime?.home}:
            {liveScore?.halfTime?.away}, {liveScore?.fullTime?.home}:{liveScore?.fullTime?.away})
          </p>
        ) : (
          <>
            <p>
              {liveScore?.extraTime?.home}:{liveScore?.extraTime?.away}
            </p>
            <p className="penalty-score">
              ({liveScore?.penaltyScore?.home}:{liveScore?.penaltyScore?.away}{' '}
              {trans('penalties-short')})
            </p>
          </>
        )}
      </div>
    );
  }
  return `${liveScore.currentScore.home}:${liveScore.currentScore.away} ${
    liveScore.halfTime
      ? `(${liveScore.halfTime.home}:${liveScore.halfTime.away}${
          liveScore.fullTime ? `, ${liveScore.fullTime.home}:${liveScore.fullTime.away}` : ''
        })`
      : ''
  }`;
}

export default Matches;
